import React, {FC, useCallback, useEffect, useRef} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  useDebounce,
  usePrevious,
} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {
  buildCancelTokenSource,
  CancelTokenSource,
} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {wrapActionCreator} from '../../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {InteractiveListItem} from '../../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {
  Skeleton,
  SkeletonColorSchemesEnum,
} from '../../../../../../libs/shared/ui/src/components/utility/skeleton';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {
  isSearchModifierToken,
  isWildcardToken,
  SearchToken,
  splitSearchQuery,
} from '../../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {SearchSuggestionsBaseProps} from '../../../../../../srcCommon/inboxes/search/searchSuggestionsHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {
  isSuggestionMatchEmail,
  isSuggestionMatchInternal,
  SearchSuggestionMatchModel,
} from '../../../../../models/api/searchSuggestionMatchModel';
import {buildPublicId} from '../../../../../models/applications/publicIdTypes';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {
  clearSearchSuggestionsAction,
  fetchSearchSuggestionsAsyncAction,
} from '../../../../../store/panels/cells/search/cellsPanelSearchActions';
import {getCellsSearchSuggestions} from '../../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {InteractiveDateTimeFormatter} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatter';
import {TeammatesFormatter} from '../../../../common/formatters/teammatesFormatter';
import {renderTextSubject} from '../../../cells/content/cell/cellMiddle';
import {SearchSuggestionsDropdownItemIcon} from './searchSuggestionsDropdownItemIcon';
import {SearchSuggestionsDropdownSection} from './searchSuggestionsDropdownSection';

/*
 * Constants.
 */

const TEAMMATES_LIMIT = 3;

// Be careful messing with this debounce value, as the impact on the number of
// queries hitting the endpoint can be unpredictable.
// For instance, in Q2 2023, the value for search suggestions was reduced from 250ms to 200ms,
// which lead to a 30%-45% increase in the number of queries hitting the endpoint.
const SEARCH_TOP_RESULTS_DEBOUNCE_WAIT_MS = 250;

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownTopResultsProps extends SearchSuggestionsBaseProps {
  activeToken: SearchToken;
  user: TeammateModel;
  teammates: ReadonlyArray<TeammateModel>;
  parameters: InboxesParameters;
  selectedSearchFilter: InboxesSearchLocationsEnum;
}

/*
 * Styles.
 */

const StyledSearchSuggestionsTopResultsItemDiv = styled.div<{$isHighlighted: boolean}>`
  display: grid;
  grid-row-gap: 4px;
  grid-template-areas:
    'icon subject date'
    '. recipients recipients';
  grid-template-columns: 25px 1fr minmax(0, auto);
  overflow: hidden;
  align-items: center;
  padding: 10px 16px;
  ${(p) =>
    p.$isHighlighted &&
    css`
      background-color: ${p.theme.greys.shade10};
    `};
`;

const StyledSearchSuggestionsTopResultsSubjectDiv = styled.div`
  grid-area: subject;
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
`;

const StyledSearchSuggestionsTopResultsDateDiv = styled.div`
  padding-left: 2px;
  grid-area: date;
  align-self: end;
  color: ${(p) => p.theme.greys.shade70};
  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  line-height: ${fontSizes.medium};
  ${ellipsis()};
`;

const StyledSearchSuggestionsTopResultsRecipientsDiv = styled.div`
  grid-area: recipients;
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  ${ellipsis()};
`;

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownTopResults: FC<
  InboxesSearchSuggestionsDropdownTopResultsProps
> = ({user, teammates, query, activeToken, parameters, selectedSearchFilter, onSuggestionSelect}) => {
  const trimmedQuery = query?.trim();
  const previousTrimmedQuery = usePrevious(trimmedQuery);

  const suggestions = useAppSelector(getCellsSearchSuggestions);

  const dispatch = useAppDispatch();
  const fetchSuggestionsAsync = wrapActionCreator(dispatch, fetchSearchSuggestionsAsyncAction);
  const clearSuggestions = wrapActionCreator(dispatch, clearSearchSuggestionsAction);

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const maybeCancelRequest = (shouldClearSuggestions?: boolean) => {
    if (shouldClearSuggestions) {
      clearSuggestions();
    }

    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  };

  useInitialEffect(() => {
    fetchItemsAsync(activeToken);

    return () => maybeCancelRequest(true);
  });

  const fetchItemsAsync = async (searchToken: SearchToken) => {
    // Cancel any existing request.
    maybeCancelRequest();

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    // If activeToken is a wildcard token, do not fetch suggestions.
    if (isWildcardToken(searchToken, query)) {
      return;
    }

    fetchSuggestionsAsync(parameters, selectedSearchFilter, query, cancelTokenSource.token);
  };

  const fetchItemsAsyncDebounced = useDebounce(fetchItemsAsync, SEARCH_TOP_RESULTS_DEBOUNCE_WAIT_MS);

  const renderTeammates = useCallback(
    (suggestion: SearchSuggestionMatchModel) => {
      const filteredTeammates = teammates.filter((teammate) =>
        (suggestion.teammates || []).includes(teammate.id),
      );

      /**
       * For internal discussions, we need to render teammate(s).
       * If filteredTeammates is empty, it means the user is the only teammate in the conversation.
       * In this case, add the user in the array to display its name.
       */
      if (filteredTeammates.length === 0) {
        filteredTeammates.push(user);
      }

      // If there's a teammate for which givenName, familyName or alias starts with query, we should render it as the first one.
      const promotedFilteredTeammates = maybePromoteTeammate(filteredTeammates, query);

      const shouldShowFirstNameOnly = promotedFilteredTeammates.length > TEAMMATES_LIMIT;

      return (
        <TeammatesFormatter
          teammates={promotedFilteredTeammates}
          limit={TEAMMATES_LIMIT}
          showFirstNameOnly={shouldShowFirstNameOnly}
        />
      );
    },
    [query, teammates, user],
  );

  useEffect(() => {
    // Fetch new items only if trimmedQuery changed.
    if (trimmedQuery !== previousTrimmedQuery) {
      fetchItemsAsyncDebounced(activeToken);
    }
  }, [activeToken, fetchItemsAsyncDebounced, previousTrimmedQuery, trimmedQuery]);

  /**
   * Return early if:
   *   - suggestions is not defined OR
   *   - query is not defined OR
   *   - trimmed query is empty OR
   *   - query is only `modifier:value` OR
   *   - activeToken is a wildcard token.
   */

  if (
    !suggestions ||
    !trimmedQuery ||
    trimmedQuery.length === 0 ||
    hasOnlyOneModifier(trimmedQuery) ||
    isWildcardToken(activeToken, query)
  ) {
    return null;
  }

  /**
   * If `suggestions.results` is not defined, it means we did not fetch suggestions yet.
   * In this case, we want to display 4 loading suggestions.
   * If `suggestions.results` is defined but its length equals 0, we also want to display this.
   */
  if (!suggestions.results || suggestions.results.length === 0) {
    if (suggestions.isLoading) {
      return (
        <SearchSuggestionsDropdownSection title={renderSectionTitle()}>
          {Array.from({length: 4}).map((_, index) => renderSkeleton(index))}
        </SearchSuggestionsDropdownSection>
      );
    }
    return null;
  }

  return (
    <SearchSuggestionsDropdownSection title={renderSectionTitle()}>
      {suggestions.results.map((result) =>
        suggestions.isLoading ? (
          renderSkeleton(result.id)
        ) : (
          <InteractiveListItem
            onSelect={(e, key) =>
              onSuggestionSelect(createOnSelectValue(result), key, {
                insertWhiteSpace: true,
                replaceWholeQuery: true,
                shouldSaveOriginalQuery: true,
                isConversationSuggestion: true,
                conversationClassificationLabels: result.classificationLabels,
                topResultsConversationId: result.id,
                query: trimmedQuery,
              })
            }
            key={result.id}
          >
            {({itemProps, isHighlighted}) => (
              <StyledSearchSuggestionsTopResultsItemDiv {...itemProps} $isHighlighted={isHighlighted}>
                <SearchSuggestionsDropdownItemIcon
                  iconName={isSuggestionMatchEmail(result) ? 'channelEmailSmall' : 'discussionFilledSmall'}
                />
                <StyledSearchSuggestionsTopResultsSubjectDiv>
                  {renderTextSubject(result.subject, result.messageType)}
                </StyledSearchSuggestionsTopResultsSubjectDiv>
                <StyledSearchSuggestionsTopResultsDateDiv>
                  <InteractiveDateTimeFormatter
                    relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
                    value={result.date}
                  />
                </StyledSearchSuggestionsTopResultsDateDiv>
                <StyledSearchSuggestionsTopResultsRecipientsDiv>
                  {isSuggestionMatchInternal(result) ? renderTeammates(result) : result.recipient}
                </StyledSearchSuggestionsTopResultsRecipientsDiv>
              </StyledSearchSuggestionsTopResultsItemDiv>
            )}
          </InteractiveListItem>
        ),
      )}
    </SearchSuggestionsDropdownSection>
  );
};

/*
 * Helpers.
 */

function hasOnlyOneModifier(query: string) {
  const queryParts = splitSearchQuery(query);
  if (queryParts.length !== 1) {
    return false;
  }

  const [{token}] = queryParts;
  return token && isSearchModifierToken(token);
}

function renderSectionTitle() {
  return <Trans id="inboxes-search-top-results">Top results</Trans>;
}

function renderSkeleton(key: number) {
  return (
    <StyledSearchSuggestionsTopResultsItemDiv key={key} $isHighlighted={false}>
      <SearchSuggestionsDropdownItemIcon isLoading />
      <StyledSearchSuggestionsTopResultsSubjectDiv>
        <Skeleton width="80%" />
      </StyledSearchSuggestionsTopResultsSubjectDiv>
      <StyledSearchSuggestionsTopResultsRecipientsDiv>
        <Skeleton width="60%" scheme={SkeletonColorSchemesEnum.GREY10} />
      </StyledSearchSuggestionsTopResultsRecipientsDiv>
    </StyledSearchSuggestionsTopResultsItemDiv>
  );
}

function createOnSelectValue(suggestion: SearchSuggestionMatchModel) {
  const publicId = buildPublicId('conversation', suggestion.id);
  return `id:${publicId}`;
}

function maybePromoteTeammate(teammates: ReadonlyArray<TeammateModel>, query?: string) {
  // If query is not defined, return the original array.
  if (!query) {
    return teammates;
  }

  // We'll stop on first match here to avoid iterating on all elements.
  const matchedTeammateIndex = teammates.findIndex(({givenName, familyName, alias}) =>
    query
      .toLowerCase()
      .split(' ')
      .some(
        (part) =>
          givenName.toLowerCase().startsWith(part) ||
          familyName.toLowerCase().startsWith(part) ||
          alias.toLowerCase().startsWith(part),
      ),
  );

  // If no teammate matches the query, return the original array.
  if (matchedTeammateIndex === -1) {
    return teammates;
  }

  // Otherwise, put matched teammate at the first position and concatenate others.
  return teammates.reduce(
    (acc, teammate, index) => (index === matchedTeammateIndex ? acc : [...acc, teammate]),
    [teammates[matchedTeammateIndex]],
  );
}
